import * as angular from 'angular';
import * as _ from 'lodash';
import { IDebugHelper } from 'v2/modules/core/debug-helper';
import { IFilterCore } from 'v2/modules/core/filters';
import { IV3ModelHelper } from 'v2/modules/core/v3-model-helper';
import { IMyEmailResource } from 'v3/modules/common/resources/v3-my-email-resource';

declare var USER_MIN_PASSWORD_LEN: number; // global vars passed in via webpack.DefinePlugin

enum OrbSlug {
  SignUp = 'sign_up',
  Verify = 'verify',
  Payment = 'payment',
  Done = 'done',
  ConfirmationTokenUsed = 'confirmation_token_used',
  WrongAccountJoinUrl = 'wrong_account_join_url',
  WrongAccountWarning = 'wrong_account_warning',
  JoinUrlNotFound = 'join_url_not_found'
}

interface IUserPartnerMemberID {
  member_id: string;
  partner_id: number;
}

interface ICtrlScope extends ng.IScope {
  routes: v3.service.AppRoutes;
  progressOrbs: string[];
  course: v3.resource.ICourse;
  loggedInUser: v3.resource.IUser;
  user: v3.resource.IUser;
  partner: v3.resource.IPartner;
  emailMatchDomain: v3.resource.IEmail;
  domains: string[];
  domainStrings: string[];
  currentDomain: string;
  form: ng.IFormController;
  stateUI: {
    loading: boolean;
    step: OrbSlug;
    emailTaken: boolean;
    error: string | null;
    domainVerificationRequired: boolean;
    paymentsRequired: boolean;
    disableEmail: boolean;
    memberIdEnabled: boolean;
    currentOrbIndex: number;
    memberIdSlug: string;
    passwordMinLength: number;
  };
  user_params: {
    email: string;
    email_username: string;
    name: string;
    password: string;
    password_confirmation: string;
    user_partner_member_ids: IUserPartnerMemberID;
  };
  createOrUpdateUser(form: ng.IFormController): void;
  resendEmail(): void;
  confirmEmail(): void;
  isOrbActive(index: number): boolean;
  isOrbComplete(index: number): boolean;
  paymentSuccessCallback(response: any): void;
  wrongAccountSignUpContinue($event: ng.IAngularEvent): void;
  signOutFromWrongAccount($event: ng.IAngularEvent): void;
  selectDomain(options: any): void;
  isInvalidName(name: string): boolean;
}

interface IRouteParams extends ng.route.IRouteParamsService {
  confirmation_token: string;
  partner_id: string;
  course_id: string;
}

interface IFilter extends IFilterCore {
  (name: 'translate'): (key: string) => string;
}

angular.module('cerego.v3Registration').controller('courseRegistrationCtrl', [
  '$scope',
  '$routeParams',
  '$window',
  'DebugHelper',
  'V3CoursesResource',
  'V3PartnerCoursesResource',
  'UserManager',
  'V3UsersResource',
  'V3CourseUsersResource',
  'V3ModelHelper',
  'V3MyEmailResource',
  '$filter',
  'V3SessionResource',
  'V3ActivationTokensResource',
  '$location',
  (
    $scope: ICtrlScope,
    $routeParams: IRouteParams,
    $window: ng.IWindowService,
    DebugHelper: IDebugHelper,
    V3CoursesResource: v3.api.IResource<v3.resource.ICourse>,
    V3PartnerCoursesResource: v3.api.IResource<v3.resource.ICourse>,
    UserManager: v3.service.IUserManager,
    V3UsersResource: v3.api.IResource<v3.resource.IUser>,
    V3CourseUsersResource,
    V3ModelHelper: IV3ModelHelper,
    V3MyEmailResource: IMyEmailResource,
    $filter: IFilter,
    V3SessionResource: v3.api.IResource<v3.resource.ISession>,
    V3ActivationTokensResource,
    $location: ng.ILocationService
  ) => {
    DebugHelper.register('courseRegistrationCtrl', $scope);

    const slugOrbs: OrbSlug[] = [];

    $scope.routes = Packs.iKnowRoutes;
    $scope.stateUI = {
      loading: false,
      step: OrbSlug.SignUp,
      emailTaken: false,
      error: null,
      domainVerificationRequired: false,
      paymentsRequired: false,
      disableEmail: false,
      memberIdEnabled: false,
      currentOrbIndex: 0,
      memberIdSlug: '',
      passwordMinLength: USER_MIN_PASSWORD_LEN
    };
    $scope.user_params = {
      email: '',
      email_username: '',
      name: '',
      password: '',
      password_confirmation: '',
      user_partner_member_ids: {
        member_id: '',
        partner_id: 0
      }
    };
    $scope.progressOrbs = [];
    $scope.domains = [];

    // quick fix for preventing spammers from entering urls in the full name field
    $scope.isInvalidName = (name: string) => {
      return name.length > 0 && name.indexOf('http') > -1;
    };

    $scope.createOrUpdateUser = form => {
      $scope.stateUI.emailTaken = false;
      form.submitted = true;
      if (
        !form.$valid ||
        $scope.user_params.password !== $scope.user_params.password_confirmation ||
        $scope.isInvalidName($scope.user_params.name)
      ) {
        return;
      }
      if ($scope.user.id) {
        $scope.stateUI.loading = true;
        return V3UsersResource.update(
          {
            id: $scope.user.id,
            name: $scope.user_params.name,
            password: $scope.user_params.password,
            password_confirmation: $scope.user_params.password_confirmation,
            activation_token: $routeParams.confirmation_token,
            activation_token_type: 'course_invitation'
          },
          { include: 'emails' },
          success => {
            $scope.user = V3ModelHelper.parseObject(success).data;
            $scope.stateUI.loading = false;
            verifyEmailAndPayment();
          },
          error => {
            $scope.stateUI.loading = false;
            $scope.stateUI.error = V3ModelHelper.humanizeV3Error(error.data);
          }
        );
      } else {
        if ($scope.user_params.email_username) {
          $scope.user_params.email = $scope.user_params.email_username + $scope.currentDomain;
        }

        // backend needs to know if this user needs verification or not
        $scope.stateUI.loading = true;
        V3UsersResource.create(
          { include: 'emails' },
          _.assignIn({ course_id: $scope.course.id, partner_id: $scope.partner?.id }, $scope.user_params),
          success => {
            $scope.user = V3ModelHelper.parseObject(success).data;
            $scope.$emit('registration:UserStatusChanged', $scope.user);
            $scope.stateUI.loading = false;
            verifyEmailAndPayment();
          },
          error => {
            if (error.data.errors.includes('already have a Cerego account')) {
              $scope.stateUI.emailTaken = true;
            }
            $scope.stateUI.loading = false;
            return ($scope.stateUI.error = V3ModelHelper.humanizeV3Error(error.data));
          }
        );
      }
    };

    $scope.resendEmail = (): void => {
      V3MyEmailResource.send_token({ id: $scope.emailMatchDomain.id }, success =>
        $window.alert("We've sent you another email with an eight-digit confirmation code.")
      );
    };

    $scope.confirmEmail = (): void => {
      // call endpoint with the code
      V3MyEmailResource.verify_token(
        { id: $scope.emailMatchDomain.id, confirmation_token: $scope.emailMatchDomain.attributes.confirmation_token },
        success => verifyCourseMembership(),
        error => {
          $scope.stateUI.loading = false;
          $scope.stateUI.error = V3ModelHelper.humanizeV3Error(error.data);
        }
      );
    };

    $scope.isOrbActive = index => {
      return index === $scope.stateUI.currentOrbIndex;
    };

    $scope.isOrbComplete = (index: number): boolean => {
      return index < $scope.stateUI.currentOrbIndex;
    };

    $scope.selectDomain = (options): void => {
      $scope.currentDomain = options.domain;
    };

    $scope.paymentSuccessCallback = (): void => {
      chooseStep(OrbSlug.Done);
    };

    $scope.wrongAccountSignUpContinue = ($event: ng.IAngularEvent): void => {
      $event.preventDefault();
      chooseStep(OrbSlug.SignUp);
    };

    $scope.signOutFromWrongAccount = ($event: ng.IAngularEvent): void => {
      $event.preventDefault();
      V3SessionResource.destroy({}, success => $window.location.reload());
    };

    const chooseStep = (step: OrbSlug): void => {
      $scope.stateUI.currentOrbIndex = _.indexOf(slugOrbs, step);
      $scope.stateUI.step = step;
    };

    const verifyCourseMembership = (): ng.IPromise<any> => {
      return V3CourseUsersResource.show(
        { course_id: $scope.course.id, id: $scope.user.id },
        success => {
          $scope.course.meta = success.data.meta;
          return verifyPayment();
        },
        error => {
          // If the signed in user isn't already in the course, check if their email is in the list of allowed domains
          if (
            $scope.user &&
            $scope.domains.length > 0 &&
            _.indexOf(
              $scope.domains,
              $scope.user.attributes != null ? $scope.user.attributes.email.split('@')[1] : undefined
            ) < 0
          ) {
            return chooseStep(OrbSlug.WrongAccountJoinUrl);
            // If we're not already in the course, let's try to add ourselves
          } else {
            return V3CourseUsersResource.create(
              {
                course_id: $scope.course.id,
                email: $scope.user.attributes.email,
                enrolled_via: 'enrolled_via_join_url',
                join_url_slug: $scope.course.attributes.slug
              },
              success => {
                $scope.course.meta = success.data.meta;
                return verifyPayment();
              },
              createError =>
                // Uh oh...
                DebugHelper.logError(createError)
            );
          }
        }
      );
    };

    const verifyEmailAndPayment = (): void => {
      if (needVerification()) {
        sendVerificationEmail();
      } else {
        verifyCourseMembership();
      }
    };

    const verifyPayment = (): void => {
      if ($scope.stateUI.paymentsRequired && $scope.course.meta['payment-required']) {
        // load the course after registration now that we have created a user
        V3CoursesResource.get({ id: $scope.course.id }, success => {
          $scope.course = V3ModelHelper.parseObject(success).data;
        });
        return chooseStep(OrbSlug.Payment);
      } else {
        return chooseStep(OrbSlug.Done);
      }
    };

    const needVerification = (): boolean => {
      if ($scope.stateUI.domainVerificationRequired) {
        $scope.emailMatchDomain = _.find($scope.user.relationships.emails.data as any, (email: v3.resource.IEmail) => {
          const domain = email.attributes.address.split('@')[1];
          return _.indexOf($scope.domains, domain) > -1;
        });
        if ($scope.emailMatchDomain && !$scope.emailMatchDomain.attributes.confirmed) {
          return true;
        }
      }
      return false;
    };

    const sendVerificationEmail = () => {
      return V3MyEmailResource.send_token({ id: $scope.emailMatchDomain.id }, success => chooseStep(OrbSlug.Verify))
        .$promise;
    };

    const initializeOrbs = (): void => {
      $scope.progressOrbs.push($filter('translate')('js.registrations.steps.sign_up'));
      slugOrbs.push(OrbSlug.SignUp);
      if ($scope.stateUI.domainVerificationRequired) {
        $scope.progressOrbs.push($filter('translate')('js.registrations.steps.verify'));
        slugOrbs.push(OrbSlug.Verify);
      }
      if ($scope.stateUI.paymentsRequired) {
        $scope.progressOrbs.push($filter('translate')('js.registrations.steps.pay'));
        slugOrbs.push(OrbSlug.Payment);
      }
      $scope.progressOrbs.push($filter('translate')('js.registrations.steps.done'));
      slugOrbs.push(OrbSlug.Done);
    };

    const initializeSteps = (): void => {
      if (
        ($scope.loggedInUser != null ? $scope.loggedInUser.id : undefined) &&
        $scope.loggedInUser.id !== $scope.user.id
      ) {
        chooseStep(OrbSlug.WrongAccountWarning);
      } else if (!($scope.user.id && $scope.user.attributes['has-password'])) {
        chooseStep(OrbSlug.SignUp);
      } else if (needVerification()) {
        sendVerificationEmail();
      } else {
        verifyCourseMembership();
      }
    };

    const initializeCourseInvite = (currentUser?: v3.resource.IUser): ng.IPromise<any> => {
      $scope.loggedInUser = currentUser;
      return V3ActivationTokensResource.get(
        {
          activation_token: $routeParams.confirmation_token,
          activation_token_type: 'course_invitation'
        },
        success => {
          $scope.user = V3ModelHelper.parseObject(success).data;
          $scope.user_params.email = $scope.user.attributes.email;
          $scope.stateUI.passwordMinLength = success.meta['min-password-length'];

          $scope.stateUI.disableEmail = true;
          $scope.$emit('registration:UserStatusChanged', $scope.user);

          // Since we're not logged in and don't have access to the real objects, pull as much as we can from the meta on activation_token
          $scope.course = {
            id: success.meta['course-id'],
            type: 'courses',
            attributes: {
              slug: success.meta['course-id'],
              name: success.meta['course-name']
            },
            relationships: {},
            meta: null
          };
          $scope.partner = {
            id: success.meta['partner-id'],
            type: 'partners',
            attributes: {
              name: success.meta['partner-name'],
              'partner-settings': {
                'payments-required': success.meta['payment-required'],
                'member-id-enabled': false,
                'member-id-slug': ''
              }
            },
            relationships: {},
            meta: null
          };
          return finalizeCourseLoad();
        },
        error => {
          // Handle this case. Tell them link is invalid or something.
          $scope.stateUI.loading = false;
          if (error.status === 404) {
            return ($scope.stateUI.step = OrbSlug.ConfirmationTokenUsed);
          } else {
            return ($scope.stateUI.error = V3ModelHelper.humanizeV3Error(error.data));
          }
        }
      );
    };

    const initializeJoinURL = (currentUser?: v3.resource.IUser): v3.api.IResponse<v3.resource.ICourse> => {
      $scope.user = currentUser;
      return V3PartnerCoursesResource.get(
        {
          partner_id: $routeParams.partner_id,
          id: $routeParams.course_id,
          include: 'partner,partner.image,partner.partner_domains'
        },
        success => {
          $scope.course = V3ModelHelper.parseObject(success).data;
          $scope.partner = $scope.course.relationships.partner.data as v3.resource.IPartner;
          if ($scope.partner.attributes['partner-settings']['email-domain-whitelisting-enabled']) {
            setWhiteListedDomains();
          }
          $scope.stateUI.domainVerificationRequired = $scope.domains.length > 0;
          return finalizeCourseLoad();
        },
        error => {
          chooseStep(OrbSlug.JoinUrlNotFound);
        }
      );
    };

    const setWhiteListedDomains = (): void => {
      $scope.domains = _.map(
        $scope.partner.relationships['partner-domains'].data as any,
        (domain: v3.resource.IPartnerDomain) => domain.attributes['domain-name']
      );
      $scope.domainStrings = _.map($scope.domains, domain => '@' + domain);
      $scope.currentDomain = $scope.domainStrings[0];
    };

    const finalizeCourseLoad = (): void => {
      $scope.stateUI.paymentsRequired = $scope.partner.attributes['partner-settings']['payments-required'];
      $scope.stateUI.memberIdEnabled = $scope.partner.attributes['partner-settings']['member-id-enabled'];
      if ($scope.stateUI.memberIdEnabled) {
        $scope.stateUI.memberIdSlug =
          $scope.partner.attributes.name + ' ' + $scope.partner.attributes['partner-settings']['member-id-slug'];
        $scope.user_params.user_partner_member_ids.partner_id = $scope.partner.id;
      }

      initializeOrbs();
      initializeSteps();
    };

    UserManager.loadUser().then(user => {
      if ($routeParams.confirmation_token) {
        initializeCourseInvite(user);
      } else {
        initializeJoinURL(user);
      }
    });
  }
]);
